import { TypeModel } from '@klant/conversational-connector/models/Type.model';
import { MessageCtaComponent } from '@klant/conversational/conversational-chat-message/message-cta/message-cta.component';

import { ChangeDetectionStrategy, Component, ComponentRef, effect, input, output, Type, viewChild } from '@angular/core';

import { ActionModel } from '../../conversational-connector/klant-conversational-connector.model';
import {
    ChatMessageModel,
    ChoicePromptMessageModel,
    CTAMessageModel,
    MessageStatusModel,
    ParticipantMessageModel,
    PlainTextMessageModel
} from '../../conversational-connector/models/ChatMessage.model';

import { ConversationalChatMessageDirective } from './conversational-chat-message.directive';
import { MessageChoicePromptComponent } from './message-choice-prompt/message-choice-prompt.component';
import { MessageDividerComponent } from './message-divider/message-divider.component';
import { MessageMessageComponent } from './message-message/message-message.component';

@Component({
    selector: 'conversational-chat-message',
    templateUrl: './conversational-chat-message.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConversationalChatMessageComponent {
    public readonly chatMessage = input.required<ChatMessageModel>();
    public readonly isLastMessage = input<boolean>(false);
    public readonly showAvatar = input<boolean>(false);

    public readonly sendAction = output<ActionModel>();

    private readonly conversationalChatMessage = viewChild.required(ConversationalChatMessageDirective);

    public constructor() {
        effect(() => {
            const chatMessage: ChatMessageModel = this.chatMessage();

            switch (chatMessage.type) {
                case TypeModel.PLAIN_TEXT_MESSAGE:
                    this.createPlainTextMessage(chatMessage as PlainTextMessageModel);
                    break;
                case TypeModel.CHOICE_PROMPT_MESSAGE:
                    this.createMessageChoicePrompt(chatMessage as ChoicePromptMessageModel);
                    break;
                case TypeModel.PARTICIPANT_MESSAGE:
                    this.createParticipantMessage(chatMessage as ParticipantMessageModel);
                    break;
                case TypeModel.CTA_MESSAGE:
                    this.createMessageCTA(chatMessage as CTAMessageModel);
                    break;
            }
        });
    }

    private createPlainTextMessage(plainTextMessage: PlainTextMessageModel): void {
        const componentRef = this.createComponentRef<MessageMessageComponent>(MessageMessageComponent);

        componentRef.setInput('text', plainTextMessage.text);
        componentRef.setInput('fromClient', plainTextMessage.fromClient);
        componentRef.setInput('showAvatar', this.showAvatar());
        componentRef.setInput('agentOrBot', plainTextMessage.serviceName);
        componentRef.setInput('occurredAt', plainTextMessage.occurredAt);
        componentRef.setInput('isMessagePending', this.isMessageStatusPending(plainTextMessage.messageStatus));
    }

    private createMessageChoicePrompt(choicePromptMessage: ChoicePromptMessageModel): void {
        const componentRef = this.createComponentRef<MessageChoicePromptComponent>(MessageChoicePromptComponent);

        componentRef.setInput('isLastMessage', this.isLastMessage());
        componentRef.setInput('messageChoicePrompt', choicePromptMessage);
        componentRef.setInput('occurredAt', choicePromptMessage.occurredAt);
        componentRef.setInput('showAvatar', this.showAvatar());

        componentRef.instance.sendAction.subscribe((action: ActionModel) => {
            this.sendAction.emit(action);
        });
    }

    private createMessageCTA(cta: CTAMessageModel): void {
        const componentRef = this.createComponentRef<MessageCtaComponent>(MessageCtaComponent);

        componentRef.setInput('buttonLink', cta.buttonLink);
        componentRef.setInput('buttonText', cta.buttonText);
    }

    private createParticipantMessage(participantMessageModel: ParticipantMessageModel): void {
        const introductionText = participantMessageModel.introductionText;
        if (introductionText) {
            const componentRef = this.createComponentRef<MessageDividerComponent>(MessageDividerComponent);
            componentRef.setInput('text', introductionText.replace('{{name}}', participantMessageModel.name));
        }
    }

    private createComponentRef<T>(component: Type<T>): ComponentRef<T> {
        const viewContainerRef = this.conversationalChatMessage().viewContainerRef;

        viewContainerRef.clear();
        return viewContainerRef.createComponent<T>(component);
    }

    private isMessageStatusPending(messageStatus?: MessageStatusModel): boolean {
        return !(messageStatus === MessageStatusModel.RECEIVED || messageStatus === MessageStatusModel.READ);
    }
}
